import { useEffect, useState } from 'react'

import {
  LIVEKIT_CAMERA_BACKGROUND_STATE_EVENT,
  readCameraBackgroundState,
  type CameraBackgroundState,
} from '../services/liveKitVideoBackground'

// What a disabled caller reads: nothing held, nothing wrong. One shared object, so
// the value is stable between renders.
const DISABLED_STATE: CameraBackgroundState = { held: false, problem: null }

/**
 * What the camera background is doing in the call: whether the camera is held
 * while its effect gets ready, and what is wrong, if anything (SC-158).
 *
 * Seeded from the last published state rather than starting empty: the
 * toolbar and the video tiles mount after the join, and a problem found during
 * it would otherwise never be shown.
 *
 * `enabled: false` is for callers that only sometimes need it, such as nameplates
 * and camera tiles, where only our own does: it then adds no listener and reports
 * "idle", so a state change does not re-render every remote one. Switched on
 * later, it reads the published state again, since changes made meanwhile were
 * not heard. Always call it; do not branch around the hook.
 */
export function useCameraBackgroundState({
  enabled = true,
}: { enabled?: boolean } = {}): CameraBackgroundState {
  const [state, setState] = useState<CameraBackgroundState>(() =>
    enabled ? readCameraBackgroundState() : DISABLED_STATE
  )
  // The copy above is not updated while disabled, so on the render that switches it on it
  // is stale. Read the published state for that render, and refresh the copy before commit
  // (adjusting state during render), so no render ever shows what was true before.
  const [wasEnabled, setWasEnabled] = useState(enabled)
  if (enabled !== wasEnabled) {
    setWasEnabled(enabled)
    if (enabled) setState(readCameraBackgroundState())
  }

  useEffect(() => {
    if (!enabled) return undefined
    const handle = (event: Event) => {
      const detail = (event as CustomEvent<CameraBackgroundState>).detail
      if (detail) setState(detail)
    }
    window.addEventListener(LIVEKIT_CAMERA_BACKGROUND_STATE_EVENT, handle)
    // Anything published between the first render and this listener.
    setState(readCameraBackgroundState())
    return () => window.removeEventListener(LIVEKIT_CAMERA_BACKGROUND_STATE_EVENT, handle)
  }, [enabled])

  if (!enabled) return DISABLED_STATE
  return wasEnabled ? state : readCameraBackgroundState()
}

export default useCameraBackgroundState
